// Client-side store for a vocabulary session.
//
// Queue model: `state.queue` is the list of item keys (`"type:id"`) the
// kid still has to master. `state.queue[0]` is the current prompt.
//
// - Correct answer: score becomes 1; on Next, the key is removed from
//   the queue.
// - Use hint: reveals the answer client-side and marks `hintUsed`. On
//   Next, the key is popped from the head and pushed to the back so the
//   kid has to answer it later (from memory) to master it.
// - Wrong answer: no queue change; kid can retry.
//
// Server is authoritative for correctness. submit_answer / use_hint are
// round-trips; nothing the client writes here can flip a wrong answer
// into a correct one.
//
// An item is either a vocabulary or a pronunciation entry. Both are
// scored and queued the same way — the server picks which shape to
// return for each entry in the `items` array.

const STORAGE_PREFIX = 'vocabulary_session:'
// Bump when the shape of a cached item changes so old browsers re-fetch
// instead of silently missing new fields. v4 introduced the mixed
// vocabulary/pronunciation items array.
const STATE_VERSION = 4

function storageKey(sessionId) {
  return `${STORAGE_PREFIX}${sessionId}`
}

export function itemKey(item) {
  if (!item) return null
  return `${item.type}:${item.id}`
}

function parseKey(key) {
  const idx = key.indexOf(':')
  if (idx < 0) return { item_type: key, item_id: null }
  return { item_type: key.slice(0, idx), item_id: Number(key.slice(idx + 1)) }
}

function emptyState(sessionId) {
  return {
    version: STATE_VERSION,
    sessionId,
    items: [],
    queue: [],
    targetScore: 0,
    // itemState[key] = { hintUsed, wrongAttempts, score }
    itemState: {}
  }
}

export function load(sessionId) {
  if (typeof window === 'undefined' || !window.localStorage) return null
  try {
    const raw = window.localStorage.getItem(storageKey(sessionId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed.version !== STATE_VERSION) return null
    return parsed
  } catch {
    return null
  }
}

export function save(sessionId, state) {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(storageKey(sessionId), JSON.stringify(state))
  } catch {
    // Quota / private mode - session still runs in-memory.
  }
}

export function clear(sessionId) {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.removeItem(storageKey(sessionId))
  } catch {
    // ignore
  }
}

export function hydrateFromServer(sessionId, { items, progress }) {
  const state = emptyState(sessionId)
  state.items = items || []
  state.targetScore = state.items.length

  const scoreByKey = new Map()
  ;(progress?.completed_items || []).forEach((c) => {
    const key = `${c.type}:${c.id}`
    scoreByKey.set(key, c.score)
    state.itemState[key] = {
      hintUsed: c.score !== 1,
      wrongAttempts: 0,
      score: c.score
    }
  })

  // Everything that isn't already mastered goes into the queue, in the
  // server-provided order.
  state.queue = state.items
    .map(itemKey)
    .filter((k) => scoreByKey.get(k) !== 1)

  return state
}

function ensureItem(state, key) {
  if (!state.itemState[key]) {
    state.itemState[key] = {
      hintUsed: false,
      wrongAttempts: 0,
      score: 0
    }
  }
  return state.itemState[key]
}

// Record a client-observed hint use. Doesn't touch the queue - the parent
// only moves on when the kid clicks "Tiếp theo".
export function markHintUsed(state, key) {
  const next = { ...state, itemState: { ...state.itemState } }
  next.itemState[key] = {
    ...ensureItem(next, key),
    hintUsed: true
  }
  return next
}

// Record what the server said about a submitted answer. On correct we
// set score=1; the queue is not touched here (advanceQueue does that on
// "Tiếp theo").
export function applyServerResult(state, key, { correct, score }) {
  const next = { ...state, itemState: { ...state.itemState } }
  const v = { ...ensureItem(next, key) }
  if (correct) {
    v.score = score
  } else {
    v.wrongAttempts = (v.wrongAttempts || 0) + 1
  }
  next.itemState[key] = v
  return next
}

// Called when the kid clicks "Tiếp theo". Pops the current head; if the
// item was mastered (score === 1) it's dropped, otherwise it goes to the
// back so the kid will have to answer it from memory later.
export function advanceQueue(state, key) {
  const next = { ...state, queue: [...state.queue] }
  const idx = next.queue.indexOf(key)
  if (idx === -1) return state
  next.queue.splice(idx, 1)
  const mastered = next.itemState[key]?.score === 1
  if (!mastered) next.queue.push(key)
  return next
}

export function currentItemKey(state) {
  return state?.queue?.[0] ?? null
}

export function currentItem(state) {
  const key = currentItemKey(state)
  if (!key || !state) return null
  return state.items.find((i) => itemKey(i) === key) || null
}

export function totalScore(state) {
  return Object.values(state.itemState).reduce((sum, v) => sum + (v.score || 0), 0)
}

export function masteredItemKeys(state) {
  return Object.entries(state.itemState)
    .filter(([, v]) => v.score === 1)
    .map(([key]) => key)
}

export function completedItems(state) {
  return Object.entries(state.itemState)
    .filter(([, v]) => v.score === 1 || v.hintUsed || v.wrongAttempts > 0)
    .map(([key, v]) => {
      const { item_type, item_id } = parseKey(key)
      return {
        type: item_type,
        id: item_id,
        score: v.score,
        mastered: v.score === 1
      }
    })
}

export function isCompleted(state) {
  return state?.queue?.length === 0
}

export async function fetchRandom(sessionId) {
  const response = await fetch(`/api/vocabularies/random?session_id=${sessionId}`)
  if (!response.ok) throw new Error(`Failed to load items: ${response.status}`)
  return response.json()
}

export async function submitAnswer(sessionId, key, answer) {
  const { item_type, item_id } = parseKey(key)
  const response = await fetch(`/api/vocabularies/submit_answer?session_id=${sessionId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ item_type, item_id, answer })
  })
  if (!response.ok) throw new Error(`Failed to submit answer: ${response.status}`)
  return response.json()
}

export async function requestHint(sessionId, key) {
  const { item_type, item_id } = parseKey(key)
  const response = await fetch(`/api/vocabularies/use_hint?session_id=${sessionId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ item_type, item_id })
  })
  if (!response.ok) throw new Error(`Failed to request hint: ${response.status}`)
  return response.json()
}
